import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Ban, Plus } from "lucide-react";
import { useState } from "react";
import { EllipsisText, ReloadButton, ReplyStatusBadge } from "@/components/common";
import { AutoPagination, DataTable, useServerTable } from "@/components/table";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  SearchBar,
  Tabs,
  TabsList,
  TabsTrigger,
  Typography,
} from "@/components/ui";
import useIsMobile from "@/components/ui/sidebar/hooks/use-is-mobile";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import AddShipmentRequestDialog from "@/features/supplier/components/add-shipment-request-dialog";
import ReplyDeliveryDateDialog from "@/features/supplier/components/reply-delivery-date-dialog";
import SupplierActionDropdown from "@/features/supplier/components/supplier-action-dropdown";
import SupplierCheckShipmentRequestsDialog from "@/features/supplier/components/supplier-check-shipment-requests-dialog";
import SupplierDeliveryDateHistoryDialog from "@/features/supplier/components/supplier-delivery-date-history-dialog";
import SupplierOrderFilterDialog from "@/features/supplier/components/supplier-order-filter-dialog";
import useSupplierPurchaseOrders from "@/features/supplier/hooks/use-supplier-purchase-orders";
import type {
  RecordStatus,
  SupplierOrder,
  SupplierReplyStatus,
  WarningStatus,
} from "@/types/supplier.type";
import { formatNumberWithCommas } from "@/utils/format-number";

export const Route = createFileRoute("/_protected/supplier/home")({
  component: RouteComponent,
});

type TabTypes = "all" | "expired" | "pending" | "replied";

const getFilterCustomPayload = (tab: TabTypes) => {
  return {
    all: {},
    expired: { warningStatus: "expired" },
    pending: { supplierReplyStatus: "pending,rejected" },
    replied: { supplierReplyStatus: "replied" },
    applied: { shipmentRequestStatus: "applied" },
  }[tab];
};

const isHighlightRow = ({ warningStatus }: SupplierOrder) => warningStatus === "expired";
const getRowId = (row: SupplierOrder) => row.id;

const columns: ColumnDef<SupplierOrder>[] = [
  {
    accessorKey: "poNo",
    header: "採購單號",
    cell: ({ row, getValue }) => {
      const recordStatus: RecordStatus = row.original.recordStatus;
      const isNew = recordStatus === "new";
      const poNo = getValue<string>();
      return (
        <Typography className="flex item-center gap-2">
          {poNo}
          {isNew && (
            <Badge variant="info" className="px-1">
              New
            </Badge>
          )}
        </Typography>
      );
    },
  },
  {
    accessorKey: "itemNo",
    header: "項次",
  },
  {
    accessorKey: "productNo",
    header: "料號",
  },
  {
    accessorKey: "productName",
    header: "品名",
    cell: ({ getValue }) => {
      const productName = getValue<string>();
      return <EllipsisText maxWidth={250}>{productName}</EllipsisText>;
    },
  },
  {
    accessorKey: "centralFactoryName",
    header: "客戶",
  },
  {
    accessorKey: "requiredDate",
    header: "需求交期",
    cell: ({ row }) => <Typography variant="mono">{row.getValue("requiredDate")}</Typography>,
  },
  {
    accessorKey: "confirmedDeliveryDate",
    header: "回覆交期",
    cell: ({ getValue, row }) => {
      const confirmedDeliveryDate = getValue<string>();

      if (!confirmedDeliveryDate) return <ReplyDeliveryDateDialog order={row.original} />;

      return <SupplierDeliveryDateHistoryDialog data={row.original} />;
    },
  },
  {
    accessorKey: "deliveryDateRecordCount",
    header: "回覆次數",
  },
  {
    accessorKey: "commitQty",
    header: "回覆數量",
    cell: ({ getValue }) => {
      const commitQty = getValue<string>();
      return <Typography variant="mono">{formatNumberWithCommas(commitQty)}</Typography>;
    },
  },
  {
    accessorKey: "orderQty",
    header: "需求量",
    cell: ({ getValue }) => {
      const orderQty = getValue<string>();
      return <Typography>{formatNumberWithCommas(orderQty)}</Typography>;
    },
  },
  {
    accessorKey: "receivedQty",
    header: "已交量",
    cell: ({ getValue }) => {
      const receivedQty = getValue<string>();
      return <Typography>{formatNumberWithCommas(receivedQty)}</Typography>;
    },
  },
  {
    accessorKey: "openQty",
    header: "未交量",
    cell: ({ getValue }) => {
      const openQty = getValue<string>();
      return <Typography>{formatNumberWithCommas(openQty)}</Typography>;
    },
  },
  {
    accessorKey: "note",
    header: "備註",
    size: 200,
  },
  {
    accessorKey: "supplierReplyStatus",
    header: "回覆狀態",
    cell: ({ getValue, row }) => {
      const confirmedDeliveryDate = row.getValue("confirmedDeliveryDate");
      const status = getValue<SupplierReplyStatus>();
      const warningStatus = getValue<WarningStatus>();

      if (warningStatus === "expired" && !confirmedDeliveryDate) {
        return <Badge variant="destructive">交期不及</Badge>;
      }

      return <ReplyStatusBadge status={status} />;
    },
  },
  {
    accessorKey: "warningStatus",
    header: "警示狀態",
    cell: ({ getValue }) => {
      const warningStatus = getValue<WarningStatus>();
      if (!warningStatus) return null;
      return <Badge variant="destructive">交期不及</Badge>;
    },
  },
  {
    accessorKey: "isLocked",
    header: "禁止交貨",
    cell: ({ getValue }) => {
      const isLocked = getValue<boolean>();
      return isLocked ? <Ban className="text-destructive h-5 w-5" /> : null;
    },
  },
  {
    id: "shipmentRequest",
    header: "交貨申請狀態",
    cell: ({ row }) => {
      const { shipmentRequestCount, isLocked, openQty } = row.original;

      if (shipmentRequestCount === 0) {
        return (
          <AddShipmentRequestDialog order={row.original}>
            {isLocked ? (
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button disabled variant="outline" size="sm">
                    <div className="flex items-center gap-2 mx-1">
                      <Ban />
                      <Typography>新增</Typography>
                    </div>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>目前為禁止交貨狀態</TooltipContent>
              </Tooltip>
            ) : (
              <Button disabled={Number(openQty) === 0} variant="outline" size="sm">
                <div className="flex items-center gap-2 mx-1">
                  <Plus className="h-6 w-6" />
                  <Typography>新增</Typography>
                </div>
              </Button>
            )}
          </AddShipmentRequestDialog>
        );
      }

      return <SupplierCheckShipmentRequestsDialog order={row.original} />;
    },
  },
];

function RouteComponent() {
  const [tab, setTab] = useState<TabTypes>("all");

  const isMobile = useIsMobile();

  const uiConfig = useAuthStore.use.uiConfig();
  const useShipmentRequestFunction = uiConfig.useShipmentRequestFunction;

  const {
    table,
    isFetching,
    globalFilter,
    setGlobalFilter,
    setSpecificFilter,
    onPaginationChange,
    refetch,
  } = useServerTable<SupplierOrder>({
    queryFn: useSupplierPurchaseOrders,
    customQueryPayload: getFilterCustomPayload(tab),
    initialPageSize: 20,
    columns,
    options: {
      highlightRow: isHighlightRow,
      selectionOptions: true,
      visibilityOptions: {
        visibilityState: {
          isLocked: useShipmentRequestFunction,
          shipmentRequest: useShipmentRequestFunction,
        },
      },
      getRowId,
    },
  });

  const selectedOrders = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
  const clearSelectedOrders = () => table.resetRowSelection();

  return (
    <section>
      <Card>
        <CardHeader>
          <title>供應商管理頁面｜Supplier Links</title>
          <Typography variant="h2">採購單列表</Typography>
        </CardHeader>

        <CardContent className="flex flex-col space-y-4">
          <div className="flex justify-between flex-wrap gap-y-4">
            <div className="flex items-center gap-2 order-2 md:order-1 w-full md:w-auto">
              <div className="flex flex-wrap items-center gap-2 w-full">
                <span className="w-full md:w-auto">
                  <SearchBar
                    className="grow"
                    placeholder="請輸入採購單號、客戶、料號或品名..."
                    value={globalFilter}
                    onChange={setGlobalFilter}
                  />
                </span>

                <SupplierActionDropdown selectedOrders={selectedOrders} />
              </div>
            </div>

            <div className="flex items-center gap-2 order-1 md:order-2 w-full md:w-auto">
              <div className="flex flex-wrap items-center gap-2 w-full">
                <div className="order-1 md:order-1 flex-shrink-0">
                  <SupplierOrderFilterDialog table={table} onApply={setSpecificFilter} />
                </div>

                <div className="order-3 md:order-2 w-full sm:flex-1 min-w-0">
                  <Tabs
                    value={tab}
                    onValueChange={(value) => {
                      setTab(value as typeof tab);
                      clearSelectedOrders();
                    }}
                    className="overflow-x-auto"
                  >
                    <TabsList className="min-w-max flex-nowrap">
                      <TabsTrigger value="all">全部</TabsTrigger>
                      <TabsTrigger value="expired">交期不及</TabsTrigger>
                      <TabsTrigger value="pending">待回覆</TabsTrigger>
                      <TabsTrigger value="replied">已回覆</TabsTrigger>
                      {useShipmentRequestFunction && (
                        <TabsTrigger value="applied">已申請交貨</TabsTrigger>
                      )}
                    </TabsList>
                  </Tabs>
                </div>

                <div className="order-2 md:order-3 flex-shrink-0">
                  <ReloadButton
                    onClick={() => {
                      refetch();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <DataTable table={table} isFetching={isFetching} />

          <AutoPagination<SupplierOrder>
            table={table}
            showTotalCount
            showSizeChanger={!isMobile}
            onPaginationChange={onPaginationChange}
          />
        </CardContent>
      </Card>
    </section>
  );
}
